// BEVEZETÉSI ÍV BANNERE (J10). Terv: ClickUp 123qmzqzk1r §9, §10.
//
// Elzárható, de ⚠️ AZ UTOLSÓ 3 NAPBAN MINDIG LÁTSZIK — aki az ív 1. napján
// becsukja, ne „váratlanul" kapja a blokkoló ablakot a hatálybalépés napján.
// A döntési logika a `shouldShowBanner`-ben van (tesztelt), itt csak a
// tárolás és a megjelenítés.
//
// ⚠️ A `localStorage` MINDEN olvasása és írása try/catch-ben: privát ablakban,
// letiltott site-adatoknál és natív WebView-ban is dobhat. Dobás esetén a
// banner LÁTSZIK — a hiba nem veheti el a tájékoztatást.
//
// ⚠️ 2. szabály: nincs riasztás-szemantikájú szín. Ez tájékoztatás, nem
// figyelmeztetés — semleges, a design-rendszeren belüli szín.

import { useState } from "react";
import { X } from "lucide-react";
import { openExternal } from "../utils/openExternal";
import { formatLegalDate } from "../utils/legalDate";
import { shouldShowBanner, type LegalBannerItem } from "../utils/legalGate";

/** Verzió-kulcsos tárolás: egy új verzió bannere NEM marad elzárva. */
function dismissKulcs(items: LegalBannerItem[]): string {
  const v = items.map((i) => `${i.docType}:${i.version}`).sort().join("|");
  return `glp.legalBannerDismissed.${v}`;
}

function olvasDismiss(kulcs: string): string | null {
  try {
    return window.localStorage.getItem(kulcs);
  } catch {
    return null;           // dobás → a banner látszik
  }
}

function irDismiss(kulcs: string, nap: string): void {
  try {
    window.localStorage.setItem(kulcs, nap);
  } catch {
    // Nem tudjuk megjegyezni — a banner a következő betöltésnél újra
    // látszik. Kellemetlen, de a fordított irány (néma elrejtés) rosszabb.
  }
}

interface Props {
  items: LegalBannerItem[];
  /** "YYYY-MM-DD", a helyi naptári nap (`localDateString`). */
  today: string;
}

export function LegalBanner({ items, today }: Props) {
  const kulcs = dismissKulcs(items);
  const [dismissedOn, setDismissedOn] = useState<string | null>(() => olvasDismiss(kulcs));

  if (items.length === 0) return null;

  // A legkorábban hatályba lépő tétel dönt: ha bármelyik dokumentum 3 napon
  // belül élesedik, a banner nem zárható el.
  const legkorabbi = items.reduce((a, b) => (a.daysUntilEffective <= b.daysUntilEffective ? a : b));
  if (!shouldShowBanner(legkorabbi, dismissedOn, today)) return null;

  const hatalybalepes = formatLegalDate(legkorabbi.effectiveFrom);
  const mindketto = items.length > 1;
  const elzarhato = legkorabbi.daysUntilEffective > 3;

  return (
    <div className="bg-gray-100 border-b border-gray-200 px-4 py-2.5 text-sm text-gray-700">
      <div className="max-w-2xl mx-auto flex items-start gap-3">
        <div className="flex-1 leading-snug">
          <p>
            {mindketto
              ? "Az adatvédelmi tájékoztató és a felhasználási feltételek "
              : `${items[0].docType === "terms" ? "A felhasználási feltételek " : "Az adatvédelmi tájékoztató "}`}
            {hatalybalepes ? `${hatalybalepes}-tól ` : ""}
            {mindketto ? "módosulnak." : "módosul."}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
            {items.map((i) => (
              <button
                key={i.docType}
                type="button"
                onClick={() => openExternal(i.url)}
                className="text-emerald-700 underline underline-offset-2"
              >
                {i.docType === "terms" ? "Felhasználási feltételek" : "Adatvédelmi tájékoztató"}
              </button>
            ))}
          </div>
        </div>
        {elzarhato && (
          <button
            type="button"
            aria-label="Értesítés elzárása"
            onClick={() => {
              irDismiss(kulcs, today);
              setDismissedOn(today);
            }}
            className="text-gray-400 hover:text-gray-600 shrink-0 p-1 -m-1"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
